import { Injectable } from '@angular/core';
import { map, Observable, take } from 'rxjs';
import { MediaManagerAssetDeleteState as MediaManagerAssetDeleteStateFromStore, MediaManagerStore } from './store';
import { BaseDomainModel } from 'src/app/models/base/base-domain-model';
import { MediaManagerAssetLibraryDto } from 'src/app/models/image/dto/media-manager-asset-library-dto';
import { MediaManagerFolderTreeNode } from 'src/app/models/media-manager/media-manager-file-system';
import { MediaManagerUploadService } from './upload/media-manager-upload.service';
import { MediaManagerFolderService } from './folder/media-manager-folder.service';
import { MediaManagerAssetService } from './asset/media-manager-asset.service';
import { MediaManagerBulkDeleteService } from './bulk-delete/media-manager-bulk-delete.service';
import { MediaManagerRemoteFolderSyncService } from './sync/media-manager-remote-folder-sync.service';
import { Asset } from 'src/app/models/image/dto/asset';
import { MediaManagerFolderDto } from 'src/app/models/image/dto/media-manager-folder-dto';
import { BudsenseFile } from 'src/app/models/shared/budsense-file';
import { UploadFilePath } from 'src/app/models/enum/dto/upload-file.path';
import { BulkEditAssetsRequest } from 'src/app/models/image/requests/bulk-edit-assets-request';
import { BulkEditAssetsResponse } from 'src/app/models/image/responses/bulk-edit-assets-response';
import { MediaManagerAssetFolderDto } from 'src/app/models/image/dto/media-manager-asset-folder-dto';

// Re-export the store's state types so existing consumers importing them from the domain model keep working.
export type MediaManagerAssetDeleteState = MediaManagerAssetDeleteStateFromStore;

/**
 * Public API surface for the Media Manager. Composes the state-only MediaManagerStore with the feature
 * services (upload, folder, asset, bulk-delete) so callers outside the media-manager package only need to
 * inject this one class. Query helpers (getAssetById$, folderExists$, ...) live here so the store can stay
 * state-only.
 *
 * State ownership lives on MediaManagerStore; this class delegates. Every mutation façade below is
 */
@Injectable() // Provided by Logged In Scope
export class MediaManagerDomainModel extends BaseDomainModel {
  constructor(
    private store: MediaManagerStore,
    private uploadService: MediaManagerUploadService,
    private folderService: MediaManagerFolderService,
    private assetService: MediaManagerAssetService,
    private bulkDeleteService: MediaManagerBulkDeleteService,
    // Injected purely to force eager instantiation so its remote-folder WS subscriptions attach the
    // moment the domain model is created. Nothing else in the DI graph references it. UploadService's
    // dependency on MediaManagerRemoteAssetSyncService already ensures the asset-sync side is eager.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _remoteFolderSync: MediaManagerRemoteFolderSyncService
  ) {
    super();
  }

  // --- Store re-exports (state) ---------------------------------------------

  public readonly filesystem$ = this.store.filesystem$;
  public readonly syncPOSAssets$ = this.store.syncPOSAssets$;

  public readonly filesystemChange$ = this.store.filesystemChange$;
  public readonly unfilteredFilesystemFolderTree$ = this.store.unfilteredFilesystemFolderTree$;

  public readonly multiFileUploadState$ = this.store.multiFileUploadState$;

  public readonly assetDeleteStates$ = this.store.assetDeleteStates$;

  public readonly transcodeProgressByMd5Hash$ = this.store.transcodeProgressByMd5Hash$;

  public activateBrandLibrary$(brandId: string): Observable<MediaManagerAssetLibraryDto> {
    return this.store.activateBrandLibrary$(brandId);
  }

  // --- Filesystem query helpers ---------------------------------------------

  getFilesystemFolderView(folderId: string): MediaManagerFolderTreeNode | null {
    const filesystem = this.store.getFilesystemValue();
    if (!filesystem) return null;
    return filesystem.getFolderView(folderId || 'ROOT');
  }

  getFilesystemFolderDepth(folderId: string): number | null {
    const filesystem = this.store.getFilesystemValue();
    if (!filesystem) return null;
    return filesystem.getFolderDepth(folderId || 'ROOT');
  }

  folderHasAnyAncestorInSet$(folderId: string, ancestorFolderIds: Set<string>): Observable<string | null> {
    return this.filesystem$.pipe(
      take(1),
      map(filesystem => {
        if (!filesystem) return null;
        return filesystem.folderHasAnyAncestorInSet(folderId, ancestorFolderIds);
      })
    );
  }

  folderExists$(folderId: string): Observable<boolean> {
    return this.filesystem$.pipe(
      take(1),
      map(filesystem => {
        if (!filesystem) return false;
        return filesystem.folderExists(folderId);
      })
    );
  }

  assetExists$(assetId: string): Observable<boolean> {
    return this.filesystem$.pipe(
      take(1),
      map(filesystem => {
        if (!filesystem) return false;
        return filesystem.assetExists(assetId);
      })
    );
  }

  getDescendantFolderIds$(folderId: string): Observable<string[]> {
    return this.filesystem$.pipe(
      take(1),
      map(filesystem => {
        if (!filesystem) return [];
        return filesystem.getDescendantFolderIds(folderId);
      })
    );
  }

  getDescendantAssetIds$(folderId: string): Observable<string[]> {
    return this.filesystem$.pipe(
      take(1),
      map(filesystem => {
        if (!filesystem) return [];
        return filesystem.getDescendantAssetIds(folderId);
      })
    );
  }

  getAssetById$(assetId: string): Observable<Asset | null> {
    return this.filesystem$.pipe(
      take(1),
      map(filesystem => {
        if (!filesystem || !assetId) return null;
        return filesystem.getAssetById(assetId);
      })
    );
  }

  getFolderById$(folderId: string): Observable<MediaManagerFolderDto | null> {
    return this.filesystem$.pipe(
      take(1),
      map(filesystem => {
        if (!filesystem || !folderId || folderId === 'ROOT') return null;
        return filesystem.getFolderById(folderId);
      })
    );
  }

  // --- Mutation façades -----------------------------------------------------

  uploadMultipleAssets$(
    files: BudsenseFile[],
    targetFolderId: string = 'ROOT',
    mediaClass: UploadFilePath = UploadFilePath.AssetLibraryPath
  ): Observable<void> {
    return this.uploadService.uploadMultipleAssets$(files, targetFolderId, mediaClass);
  }

  copyAssetToMediaManager$(sourceAsset: Asset, targetFolderId: string = 'ROOT'): Observable<void> {
    return this.uploadService.copyAssetToMediaManager$(sourceAsset, targetFolderId);
  }

  clearMultiFileUploadState(): void {
    this.uploadService.clearMultiFileUploadState();
  }

  removeFailedMultiUploadItems(): void {
    this.uploadService.removeFailedMultiUploadItems();
  }

  appendMultiUploadAssets$(
    files: BudsenseFile[],
    targetFolderId: string = 'ROOT',
    mediaClass: UploadFilePath = UploadFilePath.AssetLibraryPath
  ): Observable<void> {
    return this.uploadService.appendMultiUploadAssets$(files, targetFolderId, mediaClass);
  }

  bulkEditAssets$(req: BulkEditAssetsRequest): Observable<BulkEditAssetsResponse> {
    return this.assetService.bulkEditAssets$(req);
  }

  createAssetFolder$(req: MediaManagerFolderDto): Observable<MediaManagerAssetFolderDto> {
    return this.folderService.createAssetFolder$(req);
  }

  bulkDeleteAssets$(
    assetUniqueIds: string[],
    folderIds: string[],
    targetFolderId: string,
    deleteAllItems: boolean
  ): Observable<void> {
    return this.bulkDeleteService.bulkDeleteAssets$(assetUniqueIds, folderIds, targetFolderId, deleteAllItems);
  }

  deleteAsset$(assetUniqueId: string): Observable<string> {
    return this.assetService.deleteAsset$(assetUniqueId);
  }

  deleteAssets$(assetUniqueIds: string[]): Observable<string[]> {
    return this.assetService.deleteAssets$(assetUniqueIds);
  }

  deleteAssetFolder$(
    id: string,
    targetFolderId: string,
    deleteAllItems: boolean
  ): Observable<MediaManagerAssetFolderDto> {
    return this.folderService.deleteAssetFolder$(id, targetFolderId, deleteAllItems);
  }

  updateAssetFolder$(req: MediaManagerFolderDto): Observable<MediaManagerFolderDto> {
    return this.folderService.updateAssetFolder$(req);
  }
}
